import React from 'react';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import FormTextarea from '../../components/common/FormTextarea/FormTextarea';
import CopyTextIconButton from '../../components/common/CopyTextIconButton/CopyTextIconButton';
import Icon from '../../components/common/Icon/Icon';
import Loading from '../../components/common/Loading/Loading';
import FupSummaryCard from '../../components/common/TextSummaryCard/FupSummaryCard';
import NotesSummaryCard from '../../components/common/TextSummaryCard/NotesSummaryCard';
import TextSummaryCard from '../../components/common/TextSummaryCard/TextSummaryCard';
import NewStudentAnamnesisFupSummary from './NewStudentAnamnesisFupSummary';
import Tooltip from '../../components/common/Tooltip/Tooltip';
import { formatDateTime } from '../../utils';
import FupQuestionnaireFields from '../Fup/FupQuestionnaireFields';
import {
  composeFupResponse,
  fupAnswersToRows,
  parseFupResponseToRows,
  parseStoredFupAnswers,
  type FupQuestionnaireAnswers,
  type FupQuestionnaireErrors,
} from '../Fup/fupQuestionnaire';
import styles from './FupResponseModal.module.css';

interface FupResponseModalProps {
  open: boolean;
  studentName?: string | null;
  createdAt?: string | null;
  response?: string | null;
  storedAnswers?: Partial<FupQuestionnaireAnswers> | null;
  notesSummary?: string | null;
  fupSummary?: string | null;
  anamnesisExerciseHistory?: string | null;
  anamnesisGoalDetails?: string | null;
  anamnesisAiSummary?: string | null;
  loading?: boolean;
  loadError?: string | null;
  rows?: Array<{ label: string; value: string }>;
  editing?: boolean;
  questionnaire?: FupQuestionnaireAnswers | null;
  questionnaireErrors?: FupQuestionnaireErrors;
  onQuestionnaireChange?: <K extends keyof FupQuestionnaireAnswers>(
    field: K,
    value: FupQuestionnaireAnswers[K],
  ) => void;
  responseDraft?: string;
  responseError?: string;
  onResponseDraftChange?: (value: string) => void;
  saving?: boolean;
  onSave?: () => void;
  onCancelEdit?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  onClose: () => void;
}

const FUP_RESPONSE_MODAL_WIDTH_PX = Math.round(400 * 1.3);
const FUP_EDIT_MODAL_WIDTH_PX = 640;

const FupResponseModal: React.FC<FupResponseModalProps> = ({
  open,
  studentName,
  createdAt,
  response,
  storedAnswers = null,
  notesSummary,
  fupSummary,
  anamnesisExerciseHistory,
  anamnesisGoalDetails,
  anamnesisAiSummary,
  loading = false,
  loadError = null,
  rows: extraRows,
  editing = false,
  questionnaire = null,
  questionnaireErrors = {},
  onQuestionnaireChange,
  responseDraft = '',
  responseError,
  onResponseDraftChange,
  saving = false,
  onSave,
  onCancelEdit,
  onEdit,
  onRemove,
  onClose,
}) => {
  const viewAnswers = storedAnswers
    ? parseStoredFupAnswers(storedAnswers, response)
    : null;
  const rows = extraRows
    ?? (viewAnswers ? fupAnswersToRows(viewAnswers) : parseFupResponseToRows(response));
  const title = studentName ? `Respostas do FUP — ${studentName}` : 'Respostas do FUP';
  const fullFupValue = editing && questionnaire
    ? composeFupResponse(questionnaire)
    : editing
      ? responseDraft.trim()
      : response?.trim() || rows.map((row) => `${row.label}\n${row.value}`).join('\n\n');
  const fullFupCopyButton = (
    <CopyTextIconButton value={fullFupValue} ariaLabel="Copiar FUP completo" />
  );

  if (editing) {
    return (
      <ConfirmModal
        open={open}
        title={title}
        titleAction={fullFupCopyButton}
        variant="info"
        icon="edit"
        confirmLabel="Salvar"
        cancelLabel="Voltar"
        loading={saving}
        width={FUP_EDIT_MODAL_WIDTH_PX}
        onCancel={onCancelEdit ?? onClose}
        onConfirm={onSave ?? onClose}
      >
        <div className={styles['fup-modal-content']}>
          {questionnaire && onQuestionnaireChange ? (
            <FupQuestionnaireFields
              answers={questionnaire}
              errors={questionnaireErrors}
              disabled={saving}
              showCopyButtons
              onChange={onQuestionnaireChange}
            />
          ) : (
            <FormTextarea
              id="orchestrator-fup-response"
              label="Resposta"
              labelAction={<CopyTextIconButton value={responseDraft} ariaLabel="Copiar resposta do FUP" />}
              value={responseDraft}
              error={responseError}
              disabled={saving}
              minRows={6}
              onChange={(event) => onResponseDraftChange?.(event.target.value)}
            />
          )}
        </div>
      </ConfirmModal>
    );
  }

  return (
    <ConfirmModal
      open={open}
      title={title}
      titleAction={fullFupCopyButton}
      variant="info"
      icon="fact_check"
      confirmLabel="Fechar"
      hideCancel
      width={FUP_RESPONSE_MODAL_WIDTH_PX}
      onCancel={onClose}
      onConfirm={onClose}
    >
      <div className={styles['fup-modal-content']}>
        {loading && <Loading />}
        {!loading && loadError && <p>{loadError}</p>}
        {!loading && !loadError && createdAt && (
          <div className={styles['fup-detail-row']}>
            <div className={styles['fup-detail-label']}>
              <span>Data</span>
              <CopyTextIconButton value={formatDateTime(createdAt)} ariaLabel="Copiar data" />
            </div>
            <strong>{formatDateTime(createdAt)}</strong>
          </div>
        )}
        {!loading && !loadError && rows.map((row) => (
          <div key={`${row.label}-${row.value}`} className={styles['fup-detail-row']}>
            <div className={styles['fup-detail-label']}>
              <span>{row.label}</span>
              <CopyTextIconButton value={row.value} ariaLabel={`Copiar ${row.label.toLowerCase()}`} />
            </div>
            <strong>{row.value}</strong>
          </div>
        ))}
        {!loading && !loadError && (
          <NewStudentAnamnesisFupSummary
            exerciseHistory={anamnesisExerciseHistory}
            goalDetails={anamnesisGoalDetails}
          />
        )}
        {!loading && !loadError && anamnesisAiSummary?.trim() && (
          <TextSummaryCard
            title="Resumo da anamnese"
            value={anamnesisAiSummary}
            emptyText="Sem resumo da anamnese."
          />
        )}
        {!loading && !loadError && fupSummary !== undefined && (
          <div className={styles['fup-notes-summary']}>
            <FupSummaryCard value={fupSummary} />
          </div>
        )}
        {!loading && !loadError && notesSummary !== undefined && (
          <div className={styles['fup-notes-summary']}>
            <NotesSummaryCard value={notesSummary} />
          </div>
        )}
        {(onEdit || onRemove) && (
          <div className={styles['fup-manage-actions']}>
            {onEdit && (
              <Tooltip content="Editar">
                <button
                  type="button"
                  className={styles['fup-icon-action']}
                  aria-label="Editar"
                  onClick={onEdit}
                  disabled={saving}
                >
                  <Icon name="edit" size={18} color="var(--color-brand-primary)" />
                </button>
              </Tooltip>
            )}
            {onRemove && (
              <Tooltip content="Remover">
                <button
                  type="button"
                  className={styles['fup-icon-action']}
                  aria-label="Remover"
                  onClick={onRemove}
                  disabled={saving}
                >
                  <Icon name="delete" size={18} color="var(--color-solid-danger)" />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </ConfirmModal>
  );
};

export default FupResponseModal;
